import { faSoapNote } from "@awesome.me/kit-0572d5a008/icons/kit/custom";
import { DSDropdown } from "@components/design-system/molecules";
import { faFile } from "@fortawesome/free-solid-svg-icons";
import { faStar } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useOrgDefaultTemplates,
  useTeamDefaultTemplates,
  useTeam,
  useTemplates,
} from "@hooks";
import { Tooltip } from "@mui/material";
import { useState } from "react";

import {
  UserPermissions,
  usePremiumFeature,
} from "../../../hooks/use-premium-feature";
import { truncate } from "../../../utils";
import AutoTemplatesModal from "../../autotemplates/AutoTemplatesModal";
import PremiumFeatureGate from "../../common/premium/PremiumFeatureGate";
import UpgradeModal from "../../common/premium/UpgradeModal";
import {
  SCRIBENOTE_DEFAULTS,
  DEFAULT_RECORDING_TYPE,
} from "../../common/record/constants";

function SearchableRecordingJobTypeSelection({
  selectedJobType,
  setSelectedJobType,
  onDropdownOpenChange,
  defaultJobType = DEFAULT_RECORDING_TYPE,
  showLabel = false,
  hideScribenoteDefaults = false,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { allTemplates } = useTemplates();
  const { hasFeatureAccess } = usePremiumFeature();
  const { currentTeam } = useTeam();

  const { defaultTemplates: teamDefaults, orgUuid: orgUuidForTeam } =
    useTeamDefaultTemplates(currentTeam?.uuid);

  const { defaultTemplates: orgDefaults } =
    useOrgDefaultTemplates(orgUuidForTeam);

  const handleTemplateSelect = (template) => {
    setSelectedJobType({
      value: template.uuid || template.value || defaultJobType.value,
      name: template.name,
      icon: template.templateType === "SOAP" ? faSoapNote : faFile,
      description:
        template.description || "Unset template description",
    });
    setIsModalOpen(false);
  };

  const handleChange = (value, option) => {
    if (value === "see_all") {
      setIsModalOpen(true);
      return;
    }

    // Check if this is a premium-gated favorite and user doesn't have access
    if (
      option.group === "favorites" &&
      !hasFeatureAccess(UserPermissions.ADAPTIVE_TEMPLATES_ENABLED)
    ) {
      // Show upgrade modal instead of selecting
      setShowUpgradeModal(true);
      return;
    }

    setSelectedJobType({
      value: option.uuid || option.value,
      name: option.name,
      icon:
        option.icon ||
        (option.templateType === "SOAP" ? faSoapNote : faFile),
      description: option.description,
    });
  };

  // Combine all options: favorites, then defaults
  const favoriteTemplates = allTemplates
    .filter((t) => t.isFavourite)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((t) => ({
      ...t,
      icon: t.templateType === "SOAP" ? faSoapNote : faFile,
      group: "favorites",
    }));

  const teamTemplates = (() => {
    const templateMap = new Map();

    orgDefaults.forEach((template) => {
      templateMap.set(template.uuid, template);
    });

    teamDefaults.forEach((template) => {
      templateMap.set(template.uuid, template);
    });

    return Array.from(templateMap.values())
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((t) => ({
        ...t,
        icon: t.templateType === "SOAP" ? faSoapNote : faFile,
        group: "team",
      }));
  })();

  // Build options array with section headers
  const allOptions = [];

  if (favoriteTemplates.length > 0) {
    allOptions.push({
      isHeader: true,
      id: "favorites-header",
      title: "Favorites",
      icon: faStar,
    });
    allOptions.push(...favoriteTemplates);
  }

  if (teamTemplates.length > 0) {
    allOptions.push({
      isHeader: true,
      id: "team-defaults-header",
      title: "Team Defaults",
    });
    allOptions.push(...teamTemplates);
  }

  if (!hideScribenoteDefaults) {
    allOptions.push({
      isHeader: true,
      id: "defaults-header",
      title: "Scribenote Defaults",
    });
    allOptions.push(...SCRIBENOTE_DEFAULTS);
  }

  const renderOption = (option) => {
    const icon =
      option.icon ||
      (option.templateType === "SOAP" ? faSoapNote : faFile);
    const tooltipContent =
      typeof option.description === "string" ? (
        <>
          {option.description}
          {option.description && option.user && <br />}
          {option.user && (
            <>
              Created by: {option.user.firstName}{" "}
              {option.user.lastName}
            </>
          )}
        </>
      ) : (
        option.description
      );

    const content = (
      <div className="flex items-center min-w-0">
        <FontAwesomeIcon className="mr-2 shrink-0" icon={icon} />
        <span className="truncate">{truncate(option.name, 30)}</span>
      </div>
    );

    if (option.group === "favorites") {
      return (
        <PremiumFeatureGate
          featureName="Adaptive Templates"
          iconPosition="middle-right"
          iconSize="xs"
          className="block! w-full! relative!"
          requiredPermission={
            UserPermissions.ADAPTIVE_TEMPLATES_ENABLED
          }
        >
          <Tooltip title={tooltipContent} arrow placement="right">
            {content}
          </Tooltip>
        </PremiumFeatureGate>
      );
    }

    return (
      <Tooltip title={tooltipContent} arrow placement="right">
        {content}
      </Tooltip>
    );
  };

  const afterOptions = (
    <div
      onClick={() => setIsModalOpen(true)}
      className="sticky bottom-0 z-20 px-3 py-2 font-semibold bg-brand text-white text-center cursor-pointer hover:bg-indigo-600 transition-colors border-t border-secondary"
      data-testid="see-all-templates-option"
    >
      See All Templates
    </div>
  );

  return (
    <div className="z-10">
      <DSDropdown
        label={showLabel ? "Templates" : undefined}
        options={allOptions}
        value={
          selectedJobType?.uuid ||
          selectedJobType?.value ||
          defaultJobType.value
        }
        onChange={handleChange}
        renderButton={(selected) => (
          <span className="flex-1 min-w-0 truncate text-sm text-primary">
            {truncate(
              selected?.name ||
                selectedJobType?.name ||
                defaultJobType.name,
              15,
            )}
          </span>
        )}
        renderOption={renderOption}
        getOptionLabel={(opt) => opt.name || opt.title || ""}
        getOptionValue={(opt) => opt.uuid || opt.value || opt.id}
        getOptionTestId={(opt) =>
          opt.isHeader
            ? `template-option-header-${opt.title}`
            : `template-option-${
                opt.value || opt.name || opt.uuid || opt.id
              }`
        }
        isOptionDisabled={(opt) => opt.isHeader}
        placeholder={defaultJobType.name}
        search
        searchPlaceholder="Search templates..."
        width="w-56"
        leadingIcon={
          selectedJobType?.icon || defaultJobType.icon || faFile
        }
        dataTestId="record-templates-dropdown-button"
        afterOptions={afterOptions}
        onOpenChange={onDropdownOpenChange}
        maxHeight={300}
      />

      <AutoTemplatesModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onTemplateSelect={handleTemplateSelect}
        hideScribenoteDefaults={hideScribenoteDefaults}
      />

      <UpgradeModal
        shouldShow={showUpgradeModal}
        setShouldShow={setShowUpgradeModal}
        featureName="Adaptive Templates"
      />
    </div>
  );
}

export default function RecordingJobTypeSelection(props) {
  return <SearchableRecordingJobTypeSelection {...props} />;
}
